/**
 * Typed HTTP client for the pressentia-editorial API surface.
 *
 * Every response is the standard envelope `{ status, title, message, data }`; callers only ever
 * see `data`, typed. Design System §6: the client never returns `any`.
 *
 * One base URL, because there is one service. Pressentia signs its own tokens and verifies them, so
 * sign-in is a route on the same API as everything else.
 */

import { endSession, readToken } from 'src/lib/session';

export const API_BASE_URL: string = import.meta.env['VITE_API_BASE_URL'] ?? 'https://prod.api.pressentia.mx';

const BASE_URL = API_BASE_URL;

/** External requests are bounded — an editor waiting on a hung fetch has no way to tell. */
const REQUEST_TIMEOUT_MS = 30_000;

type ApiEnvelope<TData> = {
  status: number;
  title: string;
  message: string;
  data: TData;
};

export class ApiError extends Error {
  readonly status: number;
  readonly path: string;
  /** The envelope's short label. Kept because a screen often wants to lead with it and put the
   *  server's sentence underneath, rather than showing one long line of English. */
  readonly title: string;

  constructor(message: string, status: number, path: string, title = '') {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.path = path;
    this.title = title;
  }
}

/**
 * One fetch for every call to a service, so the session token and the timeout cannot be forgotten
 * by a new endpoint.
 *
 * A 401 ends the session before the error is thrown. The token never expires on its own, so the
 * only way to see one is a token the API now refuses — a signature it no longer accepts, or an
 * editor whose access was withdrawn. Keeping it would make every later screen fail with an
 * authorization error instead of showing the sign-in form.
 */
async function send(baseUrl: string, path: string, init?: RequestInit): Promise<Response> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  const token = readToken();

  let response: Response;
  try {
    response = await fetch(`${baseUrl}${path}`, {
      ...init,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init?.headers,
      },
    });
  } catch (cause: unknown) {
    if (cause instanceof DOMException && cause.name === 'AbortError') {
      throw new ApiError(`La petición excedió ${REQUEST_TIMEOUT_MS / 1000}s`, 0, path);
    }
    throw new ApiError('No se pudo contactar al servidor', 0, path);
  } finally {
    clearTimeout(timeout);
  }

  if (!response.ok) {
    if (response.status === 401) endSession();
    const { message, title } = await readError(response);
    throw new ApiError(message, response.status, path, title);
  }

  return response;
}

async function request<TData>(path: string, init?: RequestInit): Promise<TData> {
  const response = await send(BASE_URL, path, init);
  const envelope = (await response.json()) as ApiEnvelope<TData>;
  return envelope.data;
}

/**
 * Clip audio as an object URL.
 *
 * It used to be an `<audio src>` pointing straight at the endpoint, which stopped working the
 * moment the API required a token: an element `src` carries no Authorization header. Fetching the
 * bytes and handing the element a blob keeps the access model — the recording is never reachable
 * by a URL alone — and is the reason the caller has to revoke the URL when the clip unmounts.
 */
export async function fetchAudio(path: string): Promise<string> {
  // API Gateway REST only base64-decodes a binary response when the request's Accept header
  // matches a configured binaryMediaTypes entry. fetch defaults to `*/*`, which does not match
  // `audio/*` — the clip then arrives as base64 text and the <audio> element plays nothing.
  const response = await send(BASE_URL, path, { headers: { Accept: 'audio/wav' } });
  return URL.createObjectURL(await response.blob());
}

/** The API answers errors in the same envelope; a proxy or gateway may not. */
async function readError(response: Response): Promise<{ message: string; title: string }> {
  try {
    const body = (await response.json()) as Partial<ApiEnvelope<unknown>>;
    return { message: body.message ?? body.title ?? `HTTP ${response.status}`, title: body.title ?? '' };
  } catch {
    return { message: `HTTP ${response.status}`, title: '' };
  }
}

export function apiGet<TData>(path: string): Promise<TData> {
  return request<TData>(path);
}

export function apiPost<TData>(path: string, payload: unknown): Promise<TData> {
  return request<TData>(path, { method: 'POST', body: JSON.stringify(payload) });
}
